import React from 'react';
import classNames from "classnames";
import { useIntl } from "react-intl";
import { CloseButton, Menu, MenuButton, MenuItem, MenuItems, Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { AnchorProps } from '@headlessui/react/dist/internal/floating';
import { KEEPER_HOST } from "@/core/variables";
import { LocaleIntlKeys, LocaleType, LocaleTypes } from "@/model";
import authRepository from "@/repository/authRepository";
import { useProfile } from "@/query/profile";
import { useScenario } from "@/query/scenario";
import { useLocale } from '@/hook';
import ChevronArrowIcon from "@/component/icon/ChevronArrowIcon";
import GlobalIcon from "@/assets/svgs/global.svg";
import LockIcon from "@/assets/svgs/lock.svg";
import SettingIcon from "@/assets/svgs/setting.svg";
import UserIcon from "@/assets/svgs/user_circle.svg";


interface AccountPopoverProps {
    scenarioId?: string;
    anchor: AnchorProps;
    className?: string;
}

const AccountPopover: React.FC<AccountPopoverProps> = (props) => {
    const { scenarioId, anchor, className: propsClassName } = props;

    const intl = useIntl();

    const { data: user } = useProfile();
    const { data: scenario } = useScenario(scenarioId);

    const [placement] = (anchor as string).split(" ");

    const onClickLogout = () => {
        authRepository.logout().then(() => {
            window.location.reload();
        });
    };

    if (!user) {
        return null;
    }
    return (
        <Popover className="relative z-20">
            <PopoverButton data-testid="account-popover-trigger"
                           className="btn btn-light w-[210px] pl-3.5 pr-2.5 py-[7px] justify-between rounded-[10px] focus:outline-none"
            >
                {user.name}
                <img src={UserIcon} alt="user" className="size-6" />
            </PopoverButton>

            <PopoverPanel className={classNames(
                              "absolute z-50 w-[210px] p-5 flex flex-col gap-4 bg-white rounded-[6px] shadow-black-200 shadow-btn",
                              "origin-top-right transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0",
                              propsClassName
                          )}
                          transition
                          anchor={anchor}
            >
                <div className="flex items-center justify-between">
                    <div className="text-black-500 text-[18px] font-[500]">
                        {user.name}
                    </div>
                    {scenario && (
                        <div className={classNames(
                                 "flex-none ml-2.5 px-2 bg-chat42-10 border border-chat42 rounded-[10px]",
                                 "text-chat42 text-center text-[12px] font-[500]"
                             )}
                        >
                            {scenario.user_id === user.user_id
                                ? intl.formatMessage({ id: "i00015" })
                                : intl.formatMessage({ id: "i00016" })
                            }
                        </div>
                    )}
                </div>

                <hr className="w-full border-t-black-200"/>

                <div className="-my-2 flex flex-col">
                    <CloseButton className="popover-item popover-text-500 py-2 flex justify-start items-center gap-2 text-black-500"
                                 onClick={() => { window.open(`${KEEPER_HOST}/personal/profile`); }}
                    >
                        <img src={SettingIcon} alt=""
                             aria-hidden="true"
                             className="size-4"
                        />
                        {intl.formatMessage({ id: "i00011" })}
                    </CloseButton>
                    <CloseButton className="popover-item popover-text-500 py-2 flex justify-start items-center gap-2 text-black-500"
                                 onClick={() => { window.open(`${KEEPER_HOST}/personal/security`); }}
                    >
                        <img src={LockIcon} alt=""
                             aria-hidden="true"
                             className="size-4"
                        />
                        {intl.formatMessage({ id: "i00012" })}
                    </CloseButton>
                </div>

                <hr className="w-full border-t-black-200" />

                <LanguageMenu placement={placement} />

                <hr className="w-full border-t-black-200" />

                <CloseButton className="popover-item -my-2 py-2 w-full flex items-center justify-center"
                             onClick={onClickLogout}
                >
                    <div className="popover-text-500 text-point-red">
                        {intl.formatMessage({ id: "i00014" })}
                    </div>
                </CloseButton>
            </PopoverPanel>
        </Popover>
    );
};

const LanguageMenu: React.FC<{placement: string}> = ({ placement }) => {
    const intl = useIntl();

    const { setLocale } = useLocale();

    return (
        <Menu as="div"
              className="flex justify-between items-center"
        >
            <div className="flex items-center gap-2">
                <img src={GlobalIcon} alt=""
                     aria-hidden="true"
                     className="size-4"
                />
                <div className="popover-text-500 text-black-500">
                    {intl.formatMessage({ id: "b0025" })}
                </div>
            </div>

            <MenuButton className="popover-item flex items-center gap-1 -my-2 py-2 pl-2 text-chat42 text-[14px] font-[500]">
                {intl.formatMessage({ id: LocaleIntlKeys[intl.locale as LocaleType] })}
                <ChevronArrowIcon className="size-4"
                                  direction={placement === "bottom" ? "down" : "right"}
                />
            </MenuButton>

            <MenuItems anchor={placement === "bottom" ? "bottom" : "right start"}
                       className={classNames(
                           "flex flex-col bg-white rounded-[6px] shadow-black-200 shadow-btn outline-none text-black-500 z-50",
                           placement === "right" && "mt-[3px] [--anchor-gap:4px]"
                       )}
            >
                {LocaleTypes.map(locale => (
                    <CloseButton key={locale}>
                        <MenuItem as="div"
                                  className={classNames(
                                      "popover-item popover-text-500 pl-4 w-[76px] h-[34px] flex items-center justify-start",
                                      "hover:text-chat42 data-[focus]:text-chat42 active:text-chat42"
                                  )}
                                  onClick={() => setLocale(locale)}
                        >
                            {intl.formatMessage({ id: LocaleIntlKeys[locale] })}
                        </MenuItem>
                    </CloseButton>
                ))}
            </MenuItems>
        </Menu>
    );
};

export default AccountPopover;
